// ============================================
// FILE: src/context/ProjectFlowContext.tsx  
// FIXED VERSION - NULL-SAFE SUPABASE USAGE
// ============================================

import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';

// Safe imports - these will be used conditionally
let useHybridData: any;
let useLocation: any;

try {
  const hybridModule = require('./HybridDataProvider');
  useHybridData = hybridModule.useHybridData;
} catch (e) {
  console.log('HybridDataProvider not available, using fallback');
}

try {
  const locationModule = require('./LocationContext');
  useLocation = locationModule.useLocation;
} catch (e) {
  console.log('LocationContext not available, using fallback');
}

// ============================================
// TYPES - Complete Project Structure
// ============================================

interface Elevation {
  x: number;
  y: number;
  z: number;
  buildability: 'good' | 'moderate' | 'poor';
}

interface Room {
  name: string;
  dimensions: { 
    width: number; 
    length: number; 
    height: number;
  };
  area: number;
  type: string;
}

interface FloorPlanFile {
  id: string;
  url: string;
  name: string;
  uploadDate: Date;
}

interface SitePlanFile {
  id: string;
  url: string;
  name: string;
}

interface DroneFlight {
  id: string;
  date: Date;
  images: string[];
  analysis: Record<string, any>;
}

interface Vendor {
  id: string;
  name: string;
  trade: string;
  bid: number;
  status: string;
}

interface Phase {
  name: string;
  status: 'pending' | 'active' | 'completed';
  progress: number;
  startDate: Date | null;
  endDate: Date | null;
  budget: number;
  spent: number;
  vendors: string[];
}

interface ChangeOrder {
  id: string;
  description: string;
  amount: number;
  date: Date;
  status: 'pending' | 'approved' | 'rejected';
}

interface Rendering {
  id: string;
  url: string;
  type: string;
  prompt: string;
}

interface ProjectFlowData {
  // Project Info
  projectId: string;
  projectName: string;
  address: string;
  parcelCode: string;
  clientInfo: {
    name: string;
    email: string;
    phone: string;
  };
  
  // Floor Plans
  floorPlanData: {
    files: FloorPlanFile[];
    extractedData: {
      rooms: Room[];
      totalSqFt: number;
      bedrooms: number;
      bathrooms: number;
      stories: number;
      garageSpaces: number;
    } | null;
    aiAnalysis: {
      structuralNotes: string[];
      materialSuggestions: string[];
      costEstimate: number;
    } | null;
  };
  
  // Site Plans
  sitePlanData: {
    files: SitePlanFile[];
    parcelBoundaries: Array<{ lat: number; lng: number }>;
    topography: {
      elevations: Elevation[];
      averageSlope: number;
    };
    zoning: {
      code: string;
      setbacks: { 
        front: number; 
        rear: number; 
        left: number; 
        right: number;
      };
      maxHeight: number;
      maxCoverage: number;
      restrictions: string[];
    };
  };
  
  // 3D Placement
  housePlacementData: {
    modelId: string;
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number };
    scale: { x: number; y: number; z: number };
    complianceStatus: {
      setbacks: boolean;
      height: boolean;
      coverage: boolean;
    };
  };
  
  // Drone Data
  droneData: {
    flights: DroneFlight[];
  };
  
  // Vendors
  vendorData: {
    selected: Vendor[];
    bids: Vendor[];
  };
  
  // Phases
  phaseData: {
    current: string;
    phases: {
      [key: string]: Phase;
    };
  };
  
  // Costs
  costData: {
    totalBudget: number;
    spent: number;
    estimated: number;
    byPhase: { [key: string]: number };
    changeOrders: ChangeOrder[];
  };
  
  // AI Rendering
  renderingData: {
    images: Rendering[];
    selected: string | null;
  };
}

// ============================================
// LOCAL STORAGE HELPERS
// ============================================

class LocalStorageService {
  private static readonly PREFIX = 'homequest_';

  static saveProjectFlow(projectId: string, data: ProjectFlowData): void {
    const key = `${this.PREFIX}project_${projectId}`;
    localStorage.setItem(key, JSON.stringify(data));
  }

  static loadProjectFlow(projectId: string): ProjectFlowData | null {
    const key = `${this.PREFIX}project_${projectId}`;
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  }

  static deleteProjectFlow(projectId: string): void {
    const key = `${this.PREFIX}project_${projectId}`;
    localStorage.removeItem(key);
  }

  static getAllProjects(): string[] {
    const projects: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(`${this.PREFIX}project_`)) {
        const projectId = key.replace(`${this.PREFIX}project_`, '');
        projects.push(projectId);
      }
    }
    return projects;
  }
}

// ============================================
// API SERVICE CLASS
// ============================================

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

class ProjectFlowAPI {
  static async extractFloorPlanData(fileUrl: string): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/ai/extract-floor-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileUrl })
      });
      
      if (!response.ok) throw new Error('Failed to extract floor plan data');
      return response.json();
    } catch (error) {
      // Return mock data if API fails
      return {
        data: {
          rooms: [
            { name: 'Living Room', area: 400, type: 'living' },
            { name: 'Kitchen', area: 200, type: 'kitchen' },
            { name: 'Master Bedroom', area: 300, type: 'bedroom' }
          ],
          totalSqFt: 2500,
          bedrooms: 3,
          bathrooms: 2,
          stories: 2,
          garageSpaces: 2
        },
        analysis: {
          structuralNotes: ['Standard wood frame construction'],
          materialSuggestions: ['Hardwood floors', 'Granite countertops'],
          costEstimate: 350000
        },
        totalSqFt: 2500,
        features: {}
      };
    }
  }
  
  static async analyzeSitePlan(fileUrl: string, parcelCode: string): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/ai/analyze-site-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileUrl, parcelCode })
      });
      
      if (!response.ok) throw new Error('Failed to analyze site plan');
      return response.json();
    } catch (error) {
      // Return mock data
      return {
        boundaries: [],
        zoning: {
          code: 'R-1',
          setbacks: { front: 30, rear: 20, left: 10, right: 10 },
          maxHeight: 35,
          maxCoverage: 40
        }
      };
    }
  }
  
  static async getTopographyData(lat: number, lng: number, bounds: any): Promise<Elevation[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/topography`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat, lng, bounds })
      });
      
      if (!response.ok) throw new Error('Failed to get topography data');
      return response.json();
    } catch (error) {
      // Return mock elevations
      return Array.from({ length: 10 }, (_, i) => ({
        x: i * 10,
        y: i * 10,
        z: Math.random() * 10,
        buildability: 'good' as const
      }));
    }
  }
  
  static async analyzeCompliance(placement: any, zoning: any): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/compliance/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ placement, zoning })
      });
      
      if (!response.ok) throw new Error('Failed to check compliance');
      return response.json();
    } catch (error) {
      // Return mock compliance
      return {
        setbacks: true,
        height: true,
        coverage: true
      };
    }
  }
  
  static calculateCostEstimate(sqft: number, location: string, features: any): number {
    const baseCostPerSqft = {
      'GA': 120,
      'CA': 200,
      'NY': 180,
      'TX': 110,
      'FL': 130
    };
    
    const stateCode = location.split(',').pop()?.trim() || 'GA';
    const baseRate = baseCostPerSqft[stateCode as keyof typeof baseCostPerSqft] || 150;
    
    let multiplier = 1.0;
    if (features?.luxury) multiplier += 0.3;
    if (features?.smartHome) multiplier += 0.1;
    if (features?.sustainable) multiplier += 0.15;
    
    return Math.round(sqft * baseRate * multiplier);
  }
}

// ============================================
// CONTEXT INTERFACE
// ============================================

interface ProjectFlowContextType {
  projectFlow: ProjectFlowData;
  loading: boolean;
  error: string | null;
  createProject: (name: string, address: string, clientInfo: any) => Promise<string>;
  loadProject: (projectId: string) => Promise<void>;
  updateProject: (updates: Partial<ProjectFlowData>) => Promise<void>;
  uploadFloorPlan: (file: File) => Promise<void>;
  processFloorPlan: (fileId: string) => Promise<void>;
  saveFloorPlanData: (data: any) => void;
  uploadSitePlan: (file: File) => Promise<void>;
  analyzeSitePlan: () => Promise<void>;
  saveSitePlanData: (data: any) => void;
  updateHousePlacement: (placement: any) => void;
  checkCompliance: () => Promise<boolean>;
  uploadDroneFootage: (file: File) => Promise<void>;
  processDroneData: (flightId: string) => Promise<void>;
  selectVendor: (vendor: Vendor) => void;
  removeVendor: (vendorId: string) => void;
  updateVendorBid: (vendorId: string, bid: number) => void;
  updatePhase: (phaseName: string, updates: Partial<Phase>) => void;
  startPhase: (phaseName: string) => void;
  completePhase: (phaseName: string) => void;
  updateCosts: (costs: Partial<ProjectFlowData['costData']>) => void;
  addChangeOrder: (changeOrder: ChangeOrder) => void;
  saveRendering: (rendering: Rendering) => void;
  selectRendering: (renderingId: string) => void;
  exportProjectData: () => ProjectFlowData;
  resetProject: () => void;
}

const ProjectFlowContext = createContext<ProjectFlowContextType | undefined>(undefined);

export const useProjectFlow = () => {
  const context = useContext(ProjectFlowContext);
  if (!context) {
    throw new Error('useProjectFlow must be used within ProjectFlowProvider');
  }
  return context;
};

// ============================================
// INITIAL STATE FACTORY
// ============================================

const createInitialState = (): ProjectFlowData => ({
  projectId: '',
  projectName: '',
  address: '',
  parcelCode: '',
  clientInfo: { name: '', email: '', phone: '' },
  floorPlanData: { files: [], extractedData: null, aiAnalysis: null },
  sitePlanData: {
    files: [],
    parcelBoundaries: [],
    topography: { elevations: [], averageSlope: 0 },
    zoning: {
      code: '',
      setbacks: { front: 30, rear: 20, left: 10, right: 10 },
      maxHeight: 35,
      maxCoverage: 40,
      restrictions: []
    }
  },
  housePlacementData: {
    modelId: '',
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
    complianceStatus: { setbacks: false, height: false, coverage: false }
  },
  droneData: { flights: [] },
  vendorData: { selected: [], bids: [] },
  phaseData: {
    current: 'planning',
    phases: {
      foundation: {
        name: 'Foundation',
        status: 'pending',
        progress: 0,
        startDate: null,
        endDate: null,
        budget: 0,
        spent: 0,
        vendors: []
      },
      framing: {
        name: 'Framing',
        status: 'pending',
        progress: 0,
        startDate: null,
        endDate: null,
        budget: 0,
        spent: 0,
        vendors: []
      },
      mep: {
        name: 'MEP',
        status: 'pending',
        progress: 0,
        startDate: null,
        endDate: null,
        budget: 0,
        spent: 0,
        vendors: []
      },
      interior: {
        name: 'Interior',
        status: 'pending',
        progress: 0,
        startDate: null,
        endDate: null,
        budget: 0,
        spent: 0,
        vendors: []
      },
      finish: {
        name: 'Finish',
        status: 'pending',
        progress: 0,
        startDate: null,
        endDate: null,
        budget: 0,
        spent: 0,
        vendors: []
      }
    }
  },
  costData: {
    totalBudget: 0,
    spent: 0,
    estimated: 0,
    byPhase: {},
    changeOrders: []
  },
  renderingData: { images: [], selected: null }
});

// ============================================
// PROVIDER IMPLEMENTATION
// ============================================

export const ProjectFlowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Safe usage of contexts - they might not be available
  const hybridData = useHybridData ? useHybridData() : null;
  const location = useLocation ? useLocation() : null;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectFlow, setProjectFlow] = useState<ProjectFlowData>(createInitialState());
  
  // ============================================
  // PROJECT MANAGEMENT
  // ============================================
  
  const createProject = async (name: string, address: string, clientInfo: any): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      // CHECK TIER LIMITS BEFORE CREATING PROJECT
      if (supabase) {
        try {
          // Get current user
          const { data: { user } } = await supabase.auth.getUser();

          if (user) {
            // Get user's subscription and current project count
            const { data: subscription } = await supabase
              .from('subscriptions')
              .select('*')
              .eq('user_id', user.id)
              .eq('status', 'active')
              .single();

            // Count ONLY ACTIVE projects for this user
            const { count: projectCount } = await supabase
              .from('projects')
              .select('*', { count: 'exact', head: true })
              .eq('status', 'active'); // Only count active projects, archived don't count

            // Check limits
            const tier = subscription?.tier || 'starter';
            const maxProjects = subscription?.max_projects || 2;
            const currentCount = projectCount || 0;

            // Enforce limit (unless unlimited)
            if (maxProjects !== -1 && currentCount >= maxProjects) {
              const errorMessage = `You've reached your ${tier} plan limit of ${maxProjects} active project${maxProjects > 1 ? 's' : ''}. Archive a finished project to create a new one, or upgrade your subscription.`;
              setError(errorMessage);
              setLoading(false);
              throw new Error(errorMessage);
            }
          }
        } catch (limitError: any) {
          // If it's our limit error, rethrow it
          if (limitError.message.includes('upgrade')) {
            throw limitError;
          }
          // Otherwise, log and continue (don't block on limit check failure)
          console.warn('Tier limit check failed, proceeding:', limitError);
        }
      }

      // Update location context if available
      if (location?.updateLocation) {
        await location.updateLocation(address);
      }

      // Generate project ID
      let projectId = `project-${Date.now()}`;

      // Create project in hybrid data if available
      if (hybridData?.createProject) {
        const project = await hybridData.createProject({
          name,
          address,
          status: 'planning',
          progress: 0
        });
        projectId = project.id;
      }
      
      // Initialize project flow with real data
      const newFlow: ProjectFlowData = {
        ...projectFlow,
        projectId,
        projectName: name,
        address,
        parcelCode: location?.currentLocation?.parcelId || '',
        clientInfo,
        sitePlanData: {
          ...projectFlow.sitePlanData,
          zoning: location?.zoningData || projectFlow.sitePlanData.zoning
        }
      };
      
      setProjectFlow(newFlow);
      
      // Save to database if available, otherwise use localStorage
      if (supabase) {
        try {
          await supabase
            .from('project_flows')
            .insert({
              id: projectId,
              data: newFlow,
              created_at: new Date().toISOString()
            });
        } catch (dbError) {
          console.error('Database save failed, using localStorage:', dbError);
          LocalStorageService.saveProjectFlow(projectId, newFlow);
        }
      } else {
        // No database configured, use localStorage
        LocalStorageService.saveProjectFlow(projectId, newFlow);
      }
      
      setLoading(false);
      return projectId;
      
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };
  
  const loadProject = async (projectId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      // Try database first if available
      if (supabase) {
        const { data, error: dbError } = await supabase
          .from('project_flows')
          .select('*')
          .eq('id', projectId)
          .single();
        
        if (dbError) {
          console.error('Database load failed, trying localStorage:', dbError);
          // Try localStorage as fallback
          const localData = LocalStorageService.loadProjectFlow(projectId);
          if (localData) {
            setProjectFlow(localData);
          } else {
            throw new Error('Project not found');
          }
        } else if (data) {
          setProjectFlow(data.data);
        }
      } else {
        // No database, use localStorage
        const localData = LocalStorageService.loadProjectFlow(projectId);
        if (localData) {
          setProjectFlow(localData);
        } else {
          throw new Error('Project not found');
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const updateProject = async (updates: Partial<ProjectFlowData>) => {
    const newFlow = { ...projectFlow, ...updates };
    setProjectFlow(newFlow);
    
    // Update in storage
    if (projectFlow.projectId) {
      if (supabase) {
        try {
          await supabase
            .from('project_flows')
            .update({ 
              data: newFlow,
              updated_at: new Date().toISOString()
            })
            .eq('id', projectFlow.projectId);
        } catch (dbError) {
          console.error('Database update failed, using localStorage:', dbError);
          LocalStorageService.saveProjectFlow(projectFlow.projectId, newFlow);
        }
      } else {
        // No database, use localStorage
        LocalStorageService.saveProjectFlow(projectFlow.projectId, newFlow);
      }
      
      // Update hybrid data if available
      if (hybridData?.updateProject) {
        await hybridData.updateProject(projectFlow.projectId, {
          name: newFlow.projectName,
          address: newFlow.address,
        });
      }
    }
  };
  
  // ============================================
  // FLOOR PLANS
  // ============================================
  
  const uploadFloorPlan = async (file: File) => {
    setLoading(true);
    setError(null);
    
    try {
      let url = '';
      
      if (supabase) {
        try {
          const fileName = `${projectFlow.projectId}/${Date.now()}-${file.name}`;
          const { data, error: uploadError } = await supabase.storage
            .from('floor-plans')
            .upload(fileName, file);
          
          if (uploadError) throw uploadError;
          
          if (data) {
            const { data: { publicUrl } } = supabase.storage
              .from('floor-plans')
              .getPublicUrl(data.path);
            url = publicUrl;
          }
        } catch (storageError) {
          console.error('Storage upload failed, using local URL:', storageError);
          url = URL.createObjectURL(file);
        }
      } else {
        // No database, use local object URL
        url = URL.createObjectURL(file);
      }
      
      const newFile: FloorPlanFile = {
        id: `file-${Date.now()}`,
        url,
        name: file.name,
        uploadDate: new Date()
      };
      
      setProjectFlow(prev => ({
        ...prev,
        floorPlanData: {
          ...prev.floorPlanData,
          files: [...prev.floorPlanData.files, newFile]
        }
      }));
      
      // Process the file
      await processFloorPlan(newFile.id);
      
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const processFloorPlan = async (fileId: string) => {
    setLoading(true);
    
    try {
      const file = projectFlow.floorPlanData.files.find(f => f.id === fileId);
      if (!file) throw new Error('File not found');
      
      // Call AI API to extract floor plan data
      const extractedData = await ProjectFlowAPI.extractFloorPlanData(file.url);
      
      // Calculate cost estimate based on real data
      const costEstimate = ProjectFlowAPI.calculateCostEstimate(
        extractedData.totalSqFt,
        projectFlow.address,
        extractedData.features
      );
      
      // Update project flow with extracted data
      setProjectFlow(prev => ({
        ...prev,
        floorPlanData: {
          ...prev.floorPlanData,
          extractedData: extractedData.data,
          aiAnalysis: {
            ...extractedData.analysis,
            costEstimate
          }
        },
        costData: {
          ...prev.costData,
          estimated: costEstimate,
          totalBudget: costEstimate * 1.1, // Add 10% contingency
          byPhase: {
            foundation: costEstimate * 0.15,
            framing: costEstimate * 0.25,
            mep: costEstimate * 0.20,
            interior: costEstimate * 0.25,
            finish: costEstimate * 0.15
          }
        }
      }));
      
      // Update phase budgets
      const phaseBudgets = {
        foundation: costEstimate * 0.15,
        framing: costEstimate * 0.25,
        mep: costEstimate * 0.20,
        interior: costEstimate * 0.25,
        finish: costEstimate * 0.15
      };
      
      setProjectFlow(prev => {
        const updatedPhases = { ...prev.phaseData.phases };
        Object.keys(phaseBudgets).forEach(phase => {
          updatedPhases[phase] = {
            ...updatedPhases[phase],
            budget: phaseBudgets[phase as keyof typeof phaseBudgets]
          };
        });
        
        return {
          ...prev,
          phaseData: {
            ...prev.phaseData,
            phases: updatedPhases
          }
        };
      });
      
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const saveFloorPlanData = (data: any) => {
    setProjectFlow(prev => ({
      ...prev,
      floorPlanData: {
        ...prev.floorPlanData,
        extractedData: data.extractedData || prev.floorPlanData.extractedData,
        aiAnalysis: data.aiAnalysis || prev.floorPlanData.aiAnalysis
      }
    }));
  };
  
  // ============================================
  // SITE PLANS
  // ============================================
  
  const uploadSitePlan = async (file: File) => {
    setLoading(true);
    setError(null);
    
    try {
      let url = '';
      
      if (supabase) {
        try {
          const fileName = `${projectFlow.projectId}/site-${Date.now()}-${file.name}`;
          const { data, error: uploadError } = await supabase.storage
            .from('site-plans')
            .upload(fileName, file);
          
          if (uploadError) throw uploadError;
          
          if (data) {
            const { data: { publicUrl } } = supabase.storage
              .from('site-plans')
              .getPublicUrl(data.path);
            url = publicUrl;
          }
        } catch (storageError) {
          console.error('Storage upload failed, using local URL:', storageError);
          url = URL.createObjectURL(file);
        }
      } else {
        // No database, use local object URL
        url = URL.createObjectURL(file);
      }
      
      const newFile: SitePlanFile = {
        id: `site-${Date.now()}`,
        url,
        name: file.name
      };
      
      setProjectFlow(prev => ({
        ...prev,
        sitePlanData: {
          ...prev.sitePlanData,
          files: [...prev.sitePlanData.files, newFile]
        }
      }));
      
      await analyzeSitePlan();
      
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const analyzeSitePlan = async () => {
    setLoading(true);
    
    try {
      const lat = location?.currentLocation?.lat || 34.0234;
      const lng = location?.currentLocation?.lng || -84.6155;
      
      // Get topography data
      const elevations: Elevation[] = await ProjectFlowAPI.getTopographyData(
        lat,
        lng,
        projectFlow.sitePlanData.parcelBoundaries
      );
      
      // Calculate average slope
      let totalSlope = 0;
      let slopeCount = 0;
      
      for (let i = 1; i < elevations.length; i++) {
        const dx = elevations[i].x - elevations[i - 1].x;
        const dy = elevations[i].y - elevations[i - 1].y;
        const dz = elevations[i].z - elevations[i - 1].z;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > 0) {
          const slope = Math.abs(dz / distance) * 100;
          totalSlope += slope;
          slopeCount++;
        }
      }
      
      const averageSlope = slopeCount > 0 ? totalSlope / slopeCount : 0;
      
      // Analyze site plan with AI if files exist
      if (projectFlow.sitePlanData.files.length > 0) {
        const latestFile = projectFlow.sitePlanData.files[projectFlow.sitePlanData.files.length - 1];
        const analysis = await ProjectFlowAPI.analyzeSitePlan(
          latestFile.url,
          projectFlow.parcelCode
        );
        
        // Update with analysis results
        setProjectFlow(prev => ({
          ...prev,
          sitePlanData: {
            ...prev.sitePlanData,
            topography: {
              elevations,
              averageSlope
            },
            parcelBoundaries: analysis.boundaries || prev.sitePlanData.parcelBoundaries,
            zoning: {
              ...prev.sitePlanData.zoning,
              ...analysis.zoning
            }
          }
        }));
      } else {
        // Update with topography only
        setProjectFlow(prev => ({
          ...prev,
          sitePlanData: {
            ...prev.sitePlanData,
            topography: {
              elevations,
              averageSlope
            }
          }
        }));
      }
      
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const saveSitePlanData = (data: any) => {
    setProjectFlow(prev => ({
      ...prev,
      sitePlanData: {
        ...prev.sitePlanData,
        ...data
      }
    }));
  };
  
  // ============================================
  // 3D PLACEMENT
  // ============================================
  
  const updateHousePlacement = (placement: any) => {
    setProjectFlow(prev => ({
      ...prev,
      housePlacementData: {
        ...prev.housePlacementData,
        ...placement
      }
    }));
    
    // Check compliance automatically
    checkCompliance();
  };
  
  const checkCompliance = async (): Promise<boolean> => {
    try {
      const { housePlacementData, sitePlanData } = projectFlow;
      
      // Call compliance API
      const complianceResult = await ProjectFlowAPI.analyzeCompliance(
        housePlacementData,
        sitePlanData.zoning
      );
      
      setProjectFlow(prev => ({
        ...prev,
        housePlacementData: {
          ...prev.housePlacementData,
          complianceStatus: complianceResult
        }
      }));
      
      return Object.values(complianceResult).every(v => v);
      
    } catch (err: any) {
      console.error('Compliance check failed:', err);
      return false;
    }
  };
  
  // ============================================
  // DRONE
  // ============================================
  
  const uploadDroneFootage = async (file: File) => {
    setLoading(true);
    setError(null);
    
    try {
      let url = '';
      
      if (supabase) {
        try {
          const fileName = `${projectFlow.projectId}/drone-${Date.now()}-${file.name}`;
          const { data, error: uploadError } = await supabase.storage
            .from('drone-footage')
            .upload(fileName, file);
          
          if (uploadError) throw uploadError;
          
          if (data) {
            const { data: { publicUrl } } = supabase.storage
              .from('drone-footage')
              .getPublicUrl(data.path);
            url = publicUrl;
          }
        } catch (storageError) {
          console.error('Storage upload failed, using local URL:', storageError);
          url = URL.createObjectURL(file);
        }
      } else {
        // No database, use local object URL
        url = URL.createObjectURL(file);
      }
      
      const newFlight: DroneFlight = {
        id: `flight-${Date.now()}`,
        date: new Date(),
        images: [url],
        analysis: {}
      };
      
      setProjectFlow(prev => ({
        ...prev,
        droneData: {
          flights: [...prev.droneData.flights, newFlight]
        }
      }));
      
      await processDroneData(newFlight.id);
      
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const processDroneData = async (flightId: string) => {
    try {
      const flight = projectFlow.droneData.flights.find(f => f.id === flightId);
      if (!flight) throw new Error('Flight not found');
      
      // Mock drone analysis if API fails
      const analysis = {
        progress: 'Foundation complete',
        issues: [],
        nextSteps: ['Begin framing']
      };
      
      setProjectFlow(prev => ({
        ...prev,
        droneData: {
          flights: prev.droneData.flights.map(f =>
            f.id === flightId ? { ...f, analysis } : f
          )
        }
      }));
      
    } catch (err: any) {
      setError(err.message);
    }
  };
  
  // ============================================
  // VENDORS
  // ============================================
  
  const selectVendor = (vendor: Vendor) => {
    setProjectFlow(prev => ({
      ...prev,
      vendorData: {
        ...prev.vendorData,
        selected: [...prev.vendorData.selected, vendor]
      }
    }));
  };
  
  const removeVendor = (vendorId: string) => {
    setProjectFlow(prev => ({
      ...prev,
      vendorData: {
        ...prev.vendorData,
        selected: prev.vendorData.selected.filter(v => v.id !== vendorId)
      }
    }));
  };
  
  const updateVendorBid = (vendorId: string, bid: number) => {
    setProjectFlow(prev => ({
      ...prev,
      vendorData: {
        ...prev.vendorData,
        selected: prev.vendorData.selected.map(v =>
          v.id === vendorId ? { ...v, bid } : v
        )
      }
    }));
  };
  
  // ============================================
  // PHASES
  // ============================================
  
  const updatePhase = (phaseName: string, updates: Partial<Phase>) => {
    setProjectFlow(prev => ({
      ...prev,
      phaseData: {
        ...prev.phaseData,
        phases: {
          ...prev.phaseData.phases,
          [phaseName]: {
            ...prev.phaseData.phases[phaseName],
            ...updates
          }
        }
      }
    }));
  };
  
  const startPhase = (phaseName: string) => {
    updatePhase(phaseName, {
      status: 'active',
      startDate: new Date()
    });
    
    setProjectFlow(prev => ({
      ...prev,
      phaseData: {
        ...prev.phaseData,
        current: phaseName
      }
    }));
  };
  
  const completePhase = (phaseName: string) => {
    updatePhase(phaseName, {
      status: 'completed',
      progress: 100,
      endDate: new Date()
    });
  };
  
  // ============================================
  // COSTS
  // ============================================
  
  const updateCosts = (costs: Partial<ProjectFlowData['costData']>) => {
    setProjectFlow(prev => ({
      ...prev,
      costData: {
        ...prev.costData,
        ...costs
      }
    }));
  };
  
  const addChangeOrder = (changeOrder: ChangeOrder) => {
    setProjectFlow(prev => ({
      ...prev,
      costData: {
        ...prev.costData,
        changeOrders: [...prev.costData.changeOrders, changeOrder],
        totalBudget: prev.costData.totalBudget + changeOrder.amount
      }
    }));
  };
  
  // ============================================
  // RENDERING
  // ============================================
  
  const saveRendering = (rendering: Rendering) => {
    setProjectFlow(prev => ({
      ...prev,
      renderingData: {
        ...prev.renderingData,
        images: [...prev.renderingData.images, rendering]
      }
    }));
  };
  
  const selectRendering = (renderingId: string) => {
    setProjectFlow(prev => ({
      ...prev,
      renderingData: {
        ...prev.renderingData,
        selected: renderingId
      }
    }));
  };
  
  // ============================================
  // UTILITIES
  // ============================================
  
  const exportProjectData = (): ProjectFlowData => {
    return projectFlow;
  };
  
  const resetProject = () => {
    setProjectFlow(createInitialState());
  };
  
  // Auto-save to storage on changes
  useEffect(() => {
    if (projectFlow.projectId) {
      const saveTimeout = setTimeout(async () => {
        if (supabase) {
          try {
            await supabase
              .from('project_flows')
              .update({ 
                data: projectFlow,
                updated_at: new Date().toISOString()
              })
              .eq('id', projectFlow.projectId);
          } catch (err) {
            console.error('Auto-save to database failed, using localStorage:', err);
            LocalStorageService.saveProjectFlow(projectFlow.projectId, projectFlow);
          }
        } else {
          // No database, save to localStorage
          LocalStorageService.saveProjectFlow(projectFlow.projectId, projectFlow);
        }
      }, 2000); // Debounce saves by 2 seconds
      
      return () => clearTimeout(saveTimeout);
    }
  }, [projectFlow]);
  
  const value: ProjectFlowContextType = {
    projectFlow,
    loading,
    error,
    createProject,
    loadProject,
    updateProject,
    uploadFloorPlan,
    processFloorPlan,
    saveFloorPlanData,
    uploadSitePlan,
    analyzeSitePlan,
    saveSitePlanData,
    updateHousePlacement,
    checkCompliance,
    uploadDroneFootage,
    processDroneData,
    selectVendor,
    removeVendor,
    updateVendorBid,
    updatePhase,
    startPhase,
    completePhase,
    updateCosts,
    addChangeOrder,
    saveRendering,
    selectRendering,
    exportProjectData,
    resetProject
  };
  
  return (
    <ProjectFlowContext.Provider value={value}>
      {children}
    </ProjectFlowContext.Provider>
  );
};